import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useLocalstorageState } from 'rooks';

import QuestionnaireHeader from 'components/advice/QuestionnaireHeader';
import OnboardingCardPage from 'components/lib/layouts/OnboardingCardPage';
import OnboardingPage from 'components/lib/layouts/OnboardingPage';
import OnboardingAttribution from 'components/onboarding/OnboardingAttribution';
import OnboardingConnectAccountHypeScreen from 'components/onboarding/OnboardingConnectAccountHypeScreen';
import OnboardingConnectAccountSankey from 'components/onboarding/OnboardingConnectAccountSankey';
import OnboardingConnectSingleAccountBeforePaywall from 'components/onboarding/OnboardingConnectSingleAccountBeforePaywall';
import OnboardingConnectSingleAccountDone from 'components/onboarding/OnboardingConnectSingleAccountDone';
import OnboardingConnectSpendingAccount from 'components/onboarding/OnboardingConnectSpendingAccount';
import OnboardingCreatePassword from 'components/onboarding/OnboardingCreatePassword';
import OnboardingDemographics from 'components/onboarding/OnboardingDemographics';
import OnboardingInvitePartner from 'components/onboarding/OnboardingInvitePartner';
import OnboardingPlanSelect from 'components/onboarding/OnboardingPlanSelect';
import OnboardingPremiumUpsell from 'components/onboarding/OnboardingPremiumUpsell';
import OnboardingPriorities from 'components/onboarding/OnboardingPriorities';
import OnboardingTestimonials from 'components/onboarding/OnboardingTestimonials';
import OnboardingVerifyEmail from 'components/onboarding/OnboardingVerifyEmail';
import OnboardingWelcome from 'components/onboarding/OnboardingWelcome';
import SignupRedirect from 'components/onboarding/SignupRedirect';
import PreRegistrationAccomplish from 'components/onboarding/preRegistration/PreRegistrationAccomplish';
import PreRegistrationAwards from 'components/onboarding/preRegistration/PreRegistrationAwards';
import PreRegistrationBetterUnderstand from 'components/onboarding/preRegistration/PreRegistrationBetterUnderstand';
import { usePreRegistration } from 'components/onboarding/preRegistration/PreRegistrationContext';
import PreRegistrationFinancesComplexity from 'components/onboarding/preRegistration/PreRegistrationFinancesComplexity';
import PreRegistrationFinancialSystem from 'components/onboarding/preRegistration/PreRegistrationFinancialSystem';
import PreRegistrationMostImportant from 'components/onboarding/preRegistration/PreRegistrationMostImportant';
import PreRegistrationQuotes from 'components/onboarding/preRegistration/PreRegistrationQuotes';
import PreRegistrationSavingFor from 'components/onboarding/preRegistration/PreRegistrationSavingFor';
import PreRegistrationWeCanHelpYou from 'components/onboarding/preRegistration/PreRegistrationWeCanHelpYou';
import { AccomplishmentOption } from 'components/onboarding/preRegistration/constants';
import BlockApp from 'components/site/BlockApp';
import type { RouteFlowInfo } from 'components/utils/RouteFlow';
import RouteFlow from 'components/utils/RouteFlow';

import { clearProviderEphemeralData, setTemporaryPromoCode } from 'actions';
import getSignupBlockingMessage from 'common/lib/api/signupBlockingMessage';
import { track } from 'lib/analytics/segment';
import typewriter from 'lib/analytics/typewriter';
import api from 'lib/api';
import { useFeatureFlag } from 'lib/hooks';
import useDispatch from 'lib/hooks/useDispatch';
import useIsFeatureFlagOn from 'lib/hooks/useIsFeatureFlagOn';
import { useQueryParam } from 'lib/hooks/useQueryParams';
import { useRedirectFromInAppBrowser } from 'lib/hooks/useRedirectFromInAppBrowser';
import {
  getIsSponsor,
  getManageFinancesWithPartner,
  getProviderEphemeralData,
  getUserIsLoggedIn,
} from 'selectors';

import { OnboardingEventNames } from 'common/constants/analytics';
import routes, { externalUrls } from 'constants/routes';

interface StepProps {
  component: React.ComponentType<any>;
  route: any;
  title?: string;
  pageName: string;
  externalAuthExcluded?: boolean;
  canGetBackToPreviousStep?: boolean;
  isSkippable?: boolean;
  showIfManageFinancesWithPartner?: boolean;
  excludePageFooterNavSpacing?: boolean;
  showIfSaveGoalsAccomplishment?: boolean;
}

const PRE_REGISTRATION_STEPS: StepProps[] = [
  {
    component: PreRegistrationFinancialSystem,
    route: routes.signup.preRegistration.trackingFinances,
    title: 'Do you have a system for tracking your personal finances?',
    pageName: 'Do you have a system for tracking your personal finances?',
    canGetBackToPreviousStep: false,
  },
  {
    component: PreRegistrationBetterUnderstand,
    route: routes.signup.preRegistration.understandFinances,
    title: 'Do you want to better understand your personal finances?',
    pageName: 'Do you want to better understand your personal finances?',
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationAwards,
    route: routes.signup.preRegistration.awards,
    title: "We're glad you found us",
    pageName: "We're glad you found us",
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationAccomplish,
    route: routes.signup.preRegistration.accomplishments,
    title: 'What do you hope to accomplish with Monarch?',
    pageName: 'What do you hope to accomplish with Monarch?',
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationMostImportant,
    route: routes.signup.preRegistration.priorities,
    title: "What's most important to you?",
    pageName: "What's most important to you?",
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationSavingFor,
    route: routes.signup.preRegistration.savingGoals,
    title: 'What are you saving for?',
    pageName: 'What are you saving for?',
    canGetBackToPreviousStep: true,
    showIfSaveGoalsAccomplishment: true,
  },
  {
    component: PreRegistrationFinancesComplexity,
    route: routes.signup.preRegistration.financesComplexity,
    title: 'Do you feel your finances have become more complex over time?',
    pageName: 'Do you feel your finances have become more complex over time?',
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationQuotes,
    route: routes.signup.preRegistration.quotes,
    title: 'How Monarch has helped others',
    pageName: 'How Monarch has helped others',
    canGetBackToPreviousStep: true,
  },
  {
    component: PreRegistrationWeCanHelpYou,
    route: routes.signup.preRegistration.weCanHelpYou,
    title: "Here's how Monarch can help you",
    pageName: "Here's how Monarch can help you",
    canGetBackToPreviousStep: true,
  },
];

const ONBOARDING_STEPS: StepProps[] = [
  {
    component: OnboardingWelcome,
    route: routes.signup.onboardingWelcome,
    title: 'Start an account',
    pageName: 'Onboarding Welcome',
  },
  {
    component: OnboardingVerifyEmail,
    route: routes.signup.verifyEmail,
    title: 'Verify your email',
    pageName: 'Verify your email',
    externalAuthExcluded: true,
    canGetBackToPreviousStep: true,
  },
  {
    component: OnboardingCreatePassword,
    route: routes.signup.createPassword,
    title: 'Set your password',
    pageName: 'Onboarding Create Password',
    externalAuthExcluded: true,
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingPriorities,
    route: routes.signup.priorities,
    pageName: 'What brings you to Monarch?',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingAttribution,
    route: routes.signup.attribution,
    pageName: 'How did you hear about Monarch?',
    canGetBackToPreviousStep: true,
    isSkippable: true,
    excludePageFooterNavSpacing: true,
  },
  {
    component: OnboardingDemographics,
    route: routes.signup.demographics,
    title: 'About you',
    pageName: 'Onboarding Demographics',
    canGetBackToPreviousStep: true,
  },
  {
    component: OnboardingTestimonials,
    route: routes.signup.testimonials,
    title: 'Welcome to Monarch',
    pageName: 'Onboarding Testimonials',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingConnectSingleAccountBeforePaywall,
    route: routes.signup.connectSingleAccountBeforePaywall,
    title: 'Connect Spending Account',
    pageName: 'Onboarding Connect Spending Account',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingConnectSingleAccountDone,
    route: routes.signup.connectSingleAccountDone,
    title: 'Connect your account',
    pageName: 'Onboarding Connect Spending Account - Done',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingPremiumUpsell,
    route: routes.signup.premiumUpsell,
    title: 'How your free trial works',
    pageName: 'Onboarding Premium Upsell',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingPlanSelect,
    route: routes.signup.selectPlan,
    title: 'Select a plan',
    pageName: 'Onboarding Plan Select',
    canGetBackToPreviousStep: true,
    excludePageFooterNavSpacing: true,
  },
  {
    component: OnboardingInvitePartner,
    route: routes.signup.invitePartner,
    title: 'Invite your partner',
    pageName: 'Onboarding Invite Partner',
    canGetBackToPreviousStep: false,
    isSkippable: true,
    showIfManageFinancesWithPartner: true,
  },
  {
    component: OnboardingConnectAccountHypeScreen,
    route: routes.signup.connectSpendingAccountHype,
    title: 'Connect your account',
    pageName: 'Onboarding Connect Account Hype Screen',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingConnectSpendingAccount,
    route: routes.signup.connectSpendingAccount,
    title: 'Connect Spending Account',
    pageName: 'Onboarding Connect Spending Account',
    canGetBackToPreviousStep: false,
  },
  {
    component: OnboardingConnectAccountSankey,
    route: routes.signup.sankey,
    title: 'Sankey Chart - Onboarding',
    pageName: 'Sankey Chart - Onboarding',
    canGetBackToPreviousStep: false,
  },
];

type SignupBlockingMessage = {
  title: string;
  message: string;
  callToAction: string;
  link: string;
};

const DEFAULT_BLOCKING_MESSAGE: SignupBlockingMessage = {
  title: 'App Unavailable',
  message:
    'We apologize, but the app is temporarily unavailable. Our team is addressing the issue. Please check back soon.',
  callToAction: 'Visit our help center',
  link: externalUrls.zendeskHelpCenter,
};

const validatePromoCode = (promoCode: string) => {
  // Due to some conflict of using the query param c
  // as both to store utm campaign and promo code,
  // we need to ignore any promo codes that contains _ and -
  // because those are actually campaign names instead of promo codes.
  // this will be fixed in the future ENG-11364 by using a different param for promo codes.
  const isCampaignName = promoCode.includes('_') || promoCode.includes('-');
  return !isCampaignName;
};

const OnboardingFlow = () => {
  useRedirectFromInAppBrowser();

  /**
   * This feature flag is used to block signups with a message. We want to show the message
   * on the welcome screen, so we can block users from starting the sign up process, but
   * we don't prevent them from finishing it if they already started it.
   */
  const isSignupBlocked = useIsFeatureFlagOn('block-signups-with-message');
  const {
    treatment: preRegistrationSurveyTreatment,
    isSplitReady: isPreRegistrationSurveySplitReady,
  } = useFeatureFlag('ab-test-pre-registration-survey', {
    trackImpression: true,
    isAnonymous: true,
  });

  const isPreRegistrationSurveyOn = preRegistrationSurveyTreatment === 'on';

  const STEPS = useMemo(
    (): StepProps[] => [
      {
        component: (props) => <SignupRedirect {...props} />,
        route: () => routes.signup.path,
        pageName: 'Index of the Onboarding Flow',
      },
      ...(isPreRegistrationSurveyOn ? PRE_REGISTRATION_STEPS : []),
      ...ONBOARDING_STEPS,
    ],
    [isPreRegistrationSurveyOn, isPreRegistrationSurveySplitReady],
  );

  const dispatch = useDispatch();

  /**
   * Set coupons to be used in the onboarding flow
   */
  const promoCodeParam = useQueryParam('c');
  const referralCodeParam = useQueryParam('r');
  const [, setReferralCode] = useLocalstorageState<string | undefined>(
    'monarch_referral_code',
    undefined,
  );

  useEffect(() => {
    if (RA.isNotNil(promoCodeParam)) {
      if (validatePromoCode(promoCodeParam)) {
        dispatch(setTemporaryPromoCode(promoCodeParam));
        typewriter.webAppQueryParamPromoCodeLoaded({
          promoCode: promoCodeParam,
        });
      }
    }
  }, [promoCodeParam]);

  useEffect(() => {
    if (RA.isNotNil(referralCodeParam)) {
      setReferralCode(referralCodeParam);
    }
  }, [referralCodeParam, setReferralCode]);

  const history = useHistory();
  const { pathname } = useLocation();

  const isLoggedIn = useSelector(getUserIsLoggedIn);
  const isSponsor = useSelector(getIsSponsor);

  const manageFinancesWithPartner = useSelector(getManageFinancesWithPartner);

  const { data } = usePreRegistration();

  const { accomplishments } = data;

  const ephemeralData = useSelector(getProviderEphemeralData);
  const clearEphemeralData = useCallback(() => dispatch(clearProviderEphemeralData()), [dispatch]);

  useEffect(() => {
    if (isLoggedIn && pathname !== routes.signup.premiumUpsell()) {
      history.replace(routes.dashboard());
    }
  }, []);

  const isFromFederatedSignIn = RA.isNotNil(ephemeralData);

  const steps = useMemo(() => {
    let filteredSteps = STEPS;

    // removes steps that requires the user managing finances with partner
    if (!manageFinancesWithPartner) {
      filteredSteps = filteredSteps.filter(
        ({ showIfManageFinancesWithPartner }) => !showIfManageFinancesWithPartner,
      );
    }

    if (isFromFederatedSignIn) {
      filteredSteps = filteredSteps.filter(({ externalAuthExcluded }) => !externalAuthExcluded);
    }

    const hasSaveGoalsAccomplishment = accomplishments?.includes(AccomplishmentOption.SAVE_GOALS);

    if (!hasSaveGoalsAccomplishment) {
      filteredSteps = filteredSteps.filter(
        ({ showIfSaveGoalsAccomplishment }) => !showIfSaveGoalsAccomplishment,
      );
    }

    // Remove OnboardingPriorities step if pre-registration survey is on and split is ready
    if (isPreRegistrationSurveyOn && isPreRegistrationSurveySplitReady) {
      filteredSteps = filteredSteps.filter(
        ({ route }) => route.path !== routes.signup.priorities.path,
      );
    }

    return filteredSteps;
  }, [
    isFromFederatedSignIn,
    manageFinancesWithPartner,
    STEPS,
    accomplishments,
    isPreRegistrationSurveyOn,
    isPreRegistrationSurveySplitReady,
  ]);

  const routeSteps = useMemo(
    () => steps.map(({ component, route }) => [component, route] as RouteFlowInfo),
    [steps],
  );

  const activeStepIndex = R.clamp(
    0,
    steps.length - 1,
    steps.findIndex(({ route }) => pathname === route()),
  );

  const {
    pageName,
    title,
    canGetBackToPreviousStep,
    isSkippable,
    route,
    excludePageFooterNavSpacing,
  } = steps[activeStepIndex];

  // track the page view and prevents it from being tracked multiple times
  useEffect(() => {
    track(OnboardingEventNames.OnboardingPageViewed, {
      page: pageName,
      route: route.path,
    });
  }, [pageName, route]);

  const progress = useMemo(() => activeStepIndex / (steps.length - 1), [activeStepIndex, steps]);

  const handleFlowCompleted = useCallback(() => {
    history.push(isSponsor ? routes.advisorPortal.clients() : routes.dashboard());
    clearEphemeralData();
  }, [history, isSponsor, clearEphemeralData]);

  const isOnPaywall = pathname !== routes.signup.premiumUpsell();

  // The query param will override the default behavior of the canGetBackToPreviousStep set on the STEP.
  const queryParamBack = useQueryParam('back');

  // allow users skipping the onboarding flow - we're securing the endpoints, so they can only navigate on settings
  const queryParamSkippable = useQueryParam('skippable') === 'true';

  const abTestShouldRemoveSkipButtonFromPaywall = useIsFeatureFlagOn(
    'ab-test-remove-app-upsell-and-skip',
    {
      trackImpression: true,
    },
  );

  // The A/B test overrides the skippable query parameter.
  const shouldShowSkipButton =
    (!abTestShouldRemoveSkipButtonFromPaywall && queryParamSkippable) || isSkippable;

  /*
    If the user is in the paywall, it will redirect to the billing page.

    Otherwise, it will go to the next page of the flow unless it is the last page.
  */
  const onClickSkipButton = useMemo(() => {
    if (isOnPaywall) {
      if (activeStepIndex === steps.length - 1) {
        return handleFlowCompleted;
      }

      return () => history.push(steps[activeStepIndex + 1].route());
    } else {
      return () => history.replace(routes.settings.billing());
    }
  }, [activeStepIndex, steps, handleFlowCompleted, history]);

  const hideBackButton = queryParamBack ? queryParamBack === 'false' : !canGetBackToPreviousStep;

  const [response, setResponse] = useState<SignupBlockingMessage>();

  useEffect(() => {
    if (isSignupBlocked) {
      const fetchData = async () => {
        const signupBlockingMessage =
          await getSignupBlockingMessage(api).getSignupBlockingMessage();

        setResponse(signupBlockingMessage ?? DEFAULT_BLOCKING_MESSAGE);
      };

      fetchData();
    }
  }, [isSignupBlocked]);

  if (isSignupBlocked && activeStepIndex === 0) {
    /**
     * TODO: the naming got weird on web bc we're reusing an existing component.
     * Not worried bc we will move the dynamic config to Split at some point.
     */
    return (
      <BlockApp
        message={response?.title}
        description={response?.message}
        link={response?.link}
        callToAction={response?.callToAction}
      />
    );
  }

  const isWelcomeStep = steps[activeStepIndex].route.path === routes.signup.onboardingWelcome.path;

  const content = <RouteFlow steps={routeSteps} onComplete={handleFlowCompleted} />;

  return isWelcomeStep ? (
    <OnboardingCardPage name={pageName} parentRoute={null} overrideTitle={title}>
      {content}
    </OnboardingCardPage>
  ) : (
    <OnboardingPage
      name={pageName}
      parentRoute={null}
      overrideTitle={title}
      excludePageFooterNavSpacing={!!excludePageFooterNavSpacing}
      header={
        <QuestionnaireHeader
          progress={progress}
          showBackButton={!hideBackButton}
          showSkipButton={shouldShowSkipButton}
          onClickBackButton={() => history.goBack()}
          onClickSkipButton={onClickSkipButton}
        />
      }
    >
      {content}
    </OnboardingPage>
  );
};

export default OnboardingFlow;
